import { differenceInMinutes } from 'date-fns';
import type { EventVideoConference, EventWebinar, NodeOrganization } from 'models';
import { useMemo } from 'react';
import { generateUploadFile, getDatePickerDateType } from 'ui';
import { useGetMediaSource } from 'features/media';
import type { OrganizationOption } from '../../../components';
import { useFormatTime, useGenerateDefaultHeroImage } from '../../../hooks';
import type { EditEventFormValues } from '../EditEventForm';

type P = {
  event: EventVideoConference | EventWebinar;
  organization?: NodeOrganization;
};

const getOrganizationOption = ({
  organization,
}: {
  organization?: NodeOrganization;
}): OrganizationOption | undefined => {
  if (!organization) {
    return undefined;
  }

  return {
    label: organization.name,
    optionLabel: organization.name,
    originalOrganization: organization,
    value: organization.id,
  };
};

export const useGetInitialValues = ({ event, organization }: P) => {
  const getMediaSource = useGetMediaSource();
  const formatTime = useFormatTime();
  const defaultHeroImage = useGenerateDefaultHeroImage();

  return useMemo((): Partial<EditEventFormValues> => {
    const { channel, coverPhoto, endTime, html, name, preloadedFiles, startTime, text, type } = event;
    const heroImage = coverPhoto
      ? generateUploadFile({
          response: {
            id: coverPhoto,
            url: getMediaSource({ id: coverPhoto }),
          },
        })
      : defaultHeroImage;

    const attachments = preloadedFiles
      ? {
          fileList: preloadedFiles.map((file) => ({
            fileName: file.filename,
            name: file.filename,
            percent: 100,
            response: file,
            size: file.byteSize,
            status: 'done' as const,
            type: file.contentType,
            uid: file.id,
            url: file.url,
          })),
        }
      : undefined;

    return {
      attachments,
      duration: differenceInMinutes(endTime, startTime),
      featuredInChannels: channel?.featuredInChannels,
      heroImage,
      htmlDescription: html,
      name,
      organization: getOrganizationOption({ organization }),
      showInChannels: channel?.showInChannel,
      startDate: getDatePickerDateType(startTime),
      startTime: formatTime(startTime),
      tags: channel?.tags,
      textDescription: text,
      type,
    };
  }, [event, getMediaSource, organization, defaultHeroImage, formatTime]);
};
